import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cpu, Pause, Play, RefreshCw, Server } from "lucide-react";
import { useState } from "react";
import { request } from "../api";
import { useAuth } from "../auth";
import {
  ConfirmForm,
  EmptyState,
  ErrorState,
  LoadingState,
  Metric,
  Modal,
  PageHeader,
  Panel,
  StatusPill,
} from "../components";
import { formatTime } from "../format";
import type { EnvironmentEntry, Queue, RuntimeInstance } from "../types";

export function SystemPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [control, setControl] = useState<Queue>();
  const queues = useQuery({
    queryKey: ["queues"],
    queryFn: () => request<Queue[]>("/system/queues"),
    refetchInterval: 10_000,
  });
  const runtime = useQuery({
    queryKey: ["runtime"],
    queryFn: () => request<RuntimeInstance[]>("/system/runtime"),
    refetchInterval: 15_000,
  });
  const environment = useQuery({
    queryKey: ["environment"],
    queryFn: () => request<EnvironmentEntry[]>("/system/environment"),
  });
  const queueControl = useMutation({
    mutationFn: ({ queue, reason }: { queue: Queue; reason: string }) => request<void>(
      `/system/queues/${encodeURIComponent(queue.name)}/${queue.paused ? "resume" : "pause"}`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["queues"] });
      setControl(undefined);
    },
  });
  const canOperate = session?.user.role !== "viewer";

  return (
    <>
      <PageHeader
        eyebrow="System control"
        title="Runtime, queues, and effective environment"
        description="Live health and safe controls for the components that receive, schedule, and deliver Nexus work."
        actions={<button type="button" className="button button--secondary" onClick={() => void queryClient.invalidateQueries()}><RefreshCw size={15} /> Refresh</button>}
      />

      <Panel title="Asynq queues" meta={<span>Live snapshot</span>}>
        {queues.isLoading && <LoadingState />}
        {queues.error && <ErrorState error={queues.error} />}
        {queues.data && queues.data.length > 0 ? (
          <div className="queue-grid">
            {queues.data.map((queue) => (
              <article className="queue-card" key={queue.name}>
                <header><div><p className="eyebrow">Queue</p><h3>{queue.name}</h3></div><StatusPill value={queue.paused ? "paused" : "active"} /></header>
                <div className="queue-metrics">
                  <Metric label="Pending" value={queue.pending} />
                  <Metric label="Active" value={queue.active} />
                  <Metric label="Retry" value={queue.retry} tone={queue.retry ? "negative" : "default"} />
                  <Metric label="Archived" value={queue.archived} tone={queue.archived ? "negative" : "default"} />
                </div>
                {canOperate && (
                  <button type="button" className={`button ${queue.paused ? "button--primary" : "button--secondary"}`} onClick={() => setControl(queue)}>
                    {queue.paused ? <Play size={15} /> : <Pause size={15} />}
                    {queue.paused ? "Resume queue" : "Pause queue"}
                  </button>
                )}
              </article>
            ))}
          </div>
        ) : !queues.isLoading && (
          <EmptyState title="No Asynq queues discovered" body="Queues appear after the worker initializes its Redis connection." />
        )}
      </Panel>

      <Panel title="Runtime instances" meta={<span>Heartbeat / 15s</span>}>
        {runtime.isLoading && <LoadingState />}
        {runtime.error && <ErrorState error={runtime.error} />}
        <div className="runtime-grid">
          {runtime.data?.map((instance) => {
            const stale = Date.now() - new Date(instance.LastSeenAt).getTime() > 45_000;
            return (
              <article className="runtime-card" key={instance.ID}>
                <Server size={20} />
                <div><p className="eyebrow">{instance.Component}</p><h3>{instance.ID}</h3><span>Started {formatTime(instance.StartedAt)}</span></div>
                <StatusPill value={stale ? "unhealthy" : "healthy"} />
                <small>Last seen {formatTime(instance.LastSeenAt)}</small>
              </article>
            );
          })}
        </div>
      </Panel>

      <Panel title="Effective environment" meta={<span>Secrets are never returned</span>}>
        {environment.isLoading && <LoadingState />}
        {environment.error && <ErrorState error={environment.error} />}
        {environment.data && (
          <div className="environment-table">
            <div className="environment-table__head"><span>Variable</span><span>Category</span><span>Effective value</span><span>Source</span><span>Restart</span></div>
            {environment.data.map((entry) => (
              <div className="environment-table__row" key={entry.key}>
                <code>{entry.key}</code>
                <span>{entry.category}</span>
                <strong>{entry.secret ? (entry.configured ? "•••••••• configured" : "not configured") : (entry.value ?? "not configured")}</strong>
                <span>{entry.source}</span>
                <span>{entry.restart_required ? "Required" : "No"}</span>
              </div>
            ))}
          </div>
        )}
        <div className="inline-note"><Cpu size={16} /> Only an allowlisted catalog is exposed; arbitrary process environment variables are not enumerable here.</div>
      </Panel>

      {control && (
        <Modal title={`${control.paused ? "Resume" : "Pause"} ${control.name}`} onClose={() => setControl(undefined)}>
          <ConfirmForm
            action={`${control.paused ? "resume" : "pause"} this queue`}
            detail={control.paused ? "Pending jobs will become eligible for processing again." : "Active jobs finish, while pending work remains durable in Redis."}
            confirmLabel={control.paused ? "Resume queue" : "Pause queue"}
            busy={queueControl.isPending}
            onCancel={() => setControl(undefined)}
            onConfirm={async (reason) => queueControl.mutateAsync({ queue: control, reason })}
          />
          {queueControl.error && <div className="inline-error">{queueControl.error.message}</div>}
        </Modal>
      )}
    </>
  );
}
